import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cookieContentEN } from '../content/cookie-en';
import { cookieContentET } from '../content/cookie-et';
import { cookieContentAR } from '../content/cookie-ar';

const CookiePolicy = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Get content based on language
  const getContentByLang = () => {
    switch(currentLang) {
      case 'en': return cookieContentEN;
      case 'et': return cookieContentET;
      case 'ar': return cookieContentAR;
      default: return null; // Russian content is hardcoded below
    }
  };
  
  const content = getContentByLang();
  
  const translations = {
    ru: {
      title: 'Политика использования cookies',
      subtitle: 'Последнее обновление: 06.11.2025',
      backButton: 'Вернуться на главную'
    },
    en: {
      title: 'Cookie Policy',
      subtitle: 'Last updated: 06.11.2025',
      backButton: 'Back to main page'
    },
    et: {
      title: 'Küpsiste poliitika',
      subtitle: 'Viimati uuendatud: 06.11.2025',
      backButton: 'Tagasi avalehele'
    },
    ar: {
      title: 'سياسة ملفات تعريف الارتباط',
      subtitle: 'آخر تحديث: 06.11.2025',
      backButton: 'العودة إلى الصفحة الرئيسية'
    }
  };
  
  const lang = translations[currentLang] || translations['ru'];
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-gray-800 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <a 
            href={currentLang === 'et' ? '/' : `/${currentLang}`}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            {lang.backButton}
          </a>
          <div className="flex items-center gap-3">
            <Cookie className="w-10 h-10 text-blue-400" />
            <div>
              <h1 className="text-4xl font-bold text-white mb-2 text-left rtl:text-right">
                {content?.title || lang.title}
              </h1>
              <p className="text-gray-400 text-left rtl:text-right">
                {content?.lastUpdated || lang.subtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Render content for EN/ET/AR */}
          {content && currentLang !== 'ru' && (
            <section className="prose prose-invert max-w-none">
              {content.sections.map((section, idx) => (
                <div key={idx} className="mb-8">
                  <h3 className="text-2xl font-semibold text-white mt-8 mb-4">{section.title}</h3>
                  
                  {section.content && (
                    <p className="text-gray-300 leading-relaxed">{section.content}</p>
                  )}
                  
                  {section.types && section.types.map((type, typeIdx) => (
                    <div key={typeIdx} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-4">
                      <h4 className="text-xl font-semibold text-white mb-2">{type.name}</h4>
                      <p className="text-gray-300 mb-2">{type.description}</p>
                      <ul className="list-disc list-inside text-gray-400 space-y-1">
                        {type.items.map((item, itemIdx) => (
                          <li key={itemIdx}>
                            {typeof item === 'string' ? item : (
                              <><strong>{item.name}:</strong> {item.description}</>
                            )}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-gray-500 mt-2">
                        {currentLang === 'en' && `Retention period: ${type.retention}`}
                        {currentLang === 'et' && `Säilitamisperiood: ${type.retention}`}
                        {currentLang === 'ar' && `فترة الاحتفاظ: ${type.retention}`}
                      </p>
                      {type.privacy && (
                        <p className="text-sm text-blue-400 mt-1">
                          <a href={type.privacy} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            Privacy Policy →
                          </a>
                        </p>
                      )}
                    </div>
                  ))}
                  
                  {section.buttonText && (
                    <button 
                      onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                    >
                      {section.buttonText}
                    </button>
                  )}
                  
                  {section.intro && (
                    <>
                      <p className="text-gray-300 leading-relaxed">{section.intro}</p>
                      {section.items && (
                        <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                          {section.items.map((item, itemIdx) => (
                            <li key={itemIdx}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                  
                  {section.company && (
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mt-4">
                      <p className="text-white font-semibold">{section.company}</p>
                      <p className="text-gray-400">{section.address}</p>
                      <p className="text-gray-400 mt-2">
                        <a href="/#contact" className="text-blue-400 hover:underline">
                          {section.linkText} →
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}
          
          {/* Russian content - keep existing */}
          {currentLang === 'ru' && (
            <section className="prose prose-invert max-w-none">
              <h2 className="text-3xl font-bold text-white mb-6">Политика использования cookies</h2>
              
              <h3 className="text-2xl font-semibold text-white mt-8 mb-4">1. Что такое cookies?</h3>
              <p className="text-gray-300 leading-relaxed">
                Cookies — это небольшие текстовые файлы, которые веб-сайты сохраняют на вашем устройстве 
                для улучшения пользовательского опыта и анализа веб-трафика. Cookies не вредят вашему 
                устройству и не содержат вирусов.
              </p>

              <h3 className="text-2xl font-semibold text-white mt-8 mb-4">2. Какие cookies мы используем?</h3>
              
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-4">
                <h4 className="text-xl font-semibold text-white mb-2">Необходимые cookies</h4>
                <p className="text-gray-300 mb-2">
                  Эти cookies необходимы для работы сайта и не могут быть отключены.
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li>Управление сеансом</li>
                  <li>Безопасность и аутентификация</li>
                  <li>Сохранение данных форм</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">Срок хранения: До конца сессии</p>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-4">
                <h4 className="text-xl font-semibold text-white mb-2">Аналитические cookies</h4>
                <p className="text-gray-300 mb-2">
                  Помогают нам понять, как посетители используют сайт.
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li><strong>Google Analytics 4:</strong> Анализ трафика и поведения пользователей</li>
                  <li><strong>Hotjar:</strong> Анализ пользовательского опыта и тепловые карты</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">Срок хранения: До 2 лет</p>
                <p className="text-sm text-blue-400 mt-1">
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">
                    Google Privacy Policy →
                  </a>
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-4">
                <h4 className="text-xl font-semibold text-white mb-2">Маркетинговые cookies</h4>
                <p className="text-gray-300 mb-2">
                  Используются для таргетированной рекламы и ремаркетинга.
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li><strong>Facebook Pixel:</strong> Оптимизация рекламы и ремаркетинг</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">Срок хранения: До 2 лет</p>
                <p className="text-sm text-blue-400 mt-1">
                  <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="hover:underline">
                    Facebook Privacy Policy →
                  </a>
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-4">
                <h4 className="text-xl font-semibold text-white mb-2">Функциональные cookies</h4>
                <p className="text-gray-300 mb-2">
                  Обеспечивают расширенные функции и персонализацию.
                </p>
                <ul className="list-disc list-inside text-gray-400 space-y-1">
                  <li><strong>ChatGPT виджет:</strong> AI-ассистент</li>
                  <li>Сохранение языка и предпочтений</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">Срок хранения: До 1 года</p>
              </div>

              <h3 className="text-2xl font-semibold text-white mt-8 mb-4">3. Как управлять cookies?</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Вы можете управлять cookies через наши настройки или через настройки вашего браузера. 
                Обратите внимание, что отключение cookies может повлиять на функциональность сайта.
              </p>
              <button 
                onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
              >
                Открыть настройки cookies
              </button>

              <h3 className="text-2xl font-semibold text-white mt-8 mb-4">4. Правовая основа</h3>
              <p className="text-gray-300 leading-relaxed">
                Использование cookies основано на:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                <li>Общий регламент ЕС по защите данных (GDPR)</li>
                <li>Закон Эстонской Республики об электронной связи § 104</li>
                <li>Ваше согласие (кроме необходимых cookies)</li>
              </ul>

              <h3 className="text-2xl font-semibold text-white mt-8 mb-4">5. Контакты</h3>
              <p className="text-gray-300 leading-relaxed">
                По вопросам, касающимся cookies, свяжитесь с нами:
              </p>
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mt-4">
                <p className="text-white font-semibold">Polyus Plus OÜ</p>
                <p className="text-gray-400">Uuslinna 11, Tallinn, Estonia</p>
                <p className="text-gray-400 mt-2">
                  <a href="/#contact" className="text-blue-400 hover:underline">
                    Связаться через контактную форму →
                  </a>
                </p>
              </div>
            </section>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;
